import type { ReactNode } from "react";


/** One dynamic meta row on the card: an icon, a bold label and a value (optionally rendered as a pill). */
export interface AnimalCardMetaRow {
  /** Stable react key (the field key, e.g. "kleur"). */
  key: string;
  icon: ReactNode;
  /** Already-resolved label (i18n stays in the host), shown as "label:". */
  label: string;
  /** Already-resolved + formatted value. */
  value: string;
  /** Render the value as a tinted pill (used for the "status" / days-in-shelter row). */
  pill?: boolean;
}

export interface AnimalCardProps {
  /** Animal name (the prominent heading). */
  name: string;
  /** Breed subtitle, already formatted incl. "(kruising)". Hidden when undefined. */
  subtitle?: string;
  /** Photo URL; the camera placeholder shows when null/undefined. */
  photoUrl?: string | null;
  /** Resolved "no photo" placeholder caption. */
  noPhotoLabel: string;
  /** Verblijf-number badge text, e.g. "V42" (top-left). Hidden when null. */
  badgeNumber?: string | null;
  /** Days-in-shelter status badge over the photo, e.g. "In het asiel sinds 3 dagen". Hidden when null. */
  statusBadge?: string | null;
  /** Animal currently in a foster family → purple accent + foster badge. */
  foster?: boolean;
  /** Resolved foster badge label (e.g. "Pleeggezin"). */
  fosterLabel?: string;
  /** Dynamic meta rows, in display order (the host builds these from the chosen card fields). */
  meta?: AnimalCardMetaRow[];
  /** Footer chip label (e.g. "Diernummer"). The whole footer is hidden when undefined. */
  chipLabel?: string;
  /** Chip/passport number value. */
  chipValue?: string | null;
  /** External chip-lookup URL (petbase). When set, the value renders as a link; otherwise plain text. */
  chipHref?: string | null;
  /** Resolved "Details" button label. Only used when {@link showDetailsButton} is set. */
  detailsLabel?: string;
  /**
   * Show a "Details ›" button in the footer. Off by default: the ground-truth Jmix `DierKaart`
   * (dier-aanwezig / geplaatst) has no such button — the whole card is the click target. Opt-in for the
   * few consumers that want an explicit button.
   */
  showDetailsButton?: boolean;
  /** Open the animal detail (card click + details button). */
  onOpen?: () => void;
  /**
   * Quick toggles under the name — reachable without opening the animal. They sit in the body and
   * not over the photo: labelled controls need room, and a pill with a word in it does not fit in a
   * photo corner without covering the animal. The whole card is a click target, so whatever is
   * passed here has to stop its own click from bubbling; `AdvertentieSnelActies` does.
   */
  actions?: ReactNode;
  /** Compact density: shorter photo, no meta/footer (the "Compact" view mode). */
  compact?: boolean;
  /**
   * Een klein signaal rechtsboven op de foto (bijv. het gewichtssignaal): een symbool met een uitleg voor
   * schermlezers en als tooltip. Staat er een pleeggezin-badge, dan schuift het eronder.
   */
  signaal?: { icoon: ReactNode; label: string; toon?: "danger" | "warning" | "success" | "muted" } | null;
  className?: string;
}

/**
 * Presentational animal card for the "Dier aanwezig" overview (and reusable for any animal grid). A
 * photo banner with verblijf-number / status / foster badges, a name + breed subtitle, a column of
 * dynamic meta rows, and a footer with the chip number (optional petbase link) + a Details button.
 *
 * Prop-driven and i18n-agnostic like {@link SettingsTileCard}: the host resolves every string and the
 * display formatting (age, dates, ja/nee, "kg") and passes them in. Visual ground truth is the Jmix
 * `.da2-card` (dier-attendance-overview-view.css); styles live in ui.css, token-driven.
 */
export function AnimalCard({
  name,
  subtitle,
  photoUrl,
  noPhotoLabel,
  badgeNumber,
  statusBadge,
  foster,
  fosterLabel,
  meta,
  signaal,
  chipLabel,
  chipValue,
  chipHref,
  detailsLabel,
  showDetailsButton,
  onOpen,
  actions,
  compact,
  className,
}: AnimalCardProps) {
  const classes = ["fui-animal-card"];
  if (foster) classes.push("fui-animal-card--foster");
  if (compact) classes.push("fui-animal-card--compact");
  if (className) classes.push(className);

  return (
    <div
      className={classes.join(" ")}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={
        onOpen
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onOpen();
              }
            }
          : undefined
      }
    >
      <div className="fui-animal-card__media">
        {photoUrl ? (
          <img className="fui-animal-card__image" src={photoUrl} alt={name} />
        ) : (
          // Source parity (`.fotoPlaceholderImg`): the camera-with-paw illustration centered on the
          // gradient, no caption — matches acc's "Foto niet beschikbaar" card.
          <img
            className="fui-animal-card__placeholder-img"
            src="/themes/backoffice/images/foto-niet-beschikbaar.png"
            alt={noPhotoLabel}
          />
        )}
        {badgeNumber ? <span className="fui-animal-card__id">{badgeNumber}</span> : null}
        {foster && fosterLabel ? (
          <span className="fui-animal-card__foster-badge">{fosterLabel}</span>
        ) : null}
        {statusBadge ? <span className="fui-animal-card__status-badge">{statusBadge}</span> : null}
        {signaal ? (
          <span
            className={`fui-animal-card__signaal fui-animal-card__signaal--${signaal.toon ?? "muted"}${foster && fosterLabel ? " fui-animal-card__signaal--onder" : ""}`}
            title={signaal.label}
            aria-label={signaal.label}
            role="img"
          >
            {signaal.icoon}
          </span>
        ) : null}
      </div>

      <div className="fui-animal-card__body">
        <span className="fui-animal-card__name">{name}</span>
        {subtitle !== undefined ? (
          <span className="fui-animal-card__breed">{subtitle}</span>
        ) : null}

        {actions ? <div className="fui-animal-card__actions">{actions}</div> : null}

        {meta && meta.length > 0 ? (
          <div className="fui-animal-card__meta">
            {meta.map((row) => (
              <div key={row.key} className="fui-animal-card__meta-row">
                <span className="fui-animal-card__meta-icon" aria-hidden="true">
                  {row.icon}
                </span>
                <span className="fui-animal-card__meta-label">{row.label}:</span>
                <span
                  className={
                    row.pill
                      ? "fui-animal-card__meta-value fui-animal-card__pill"
                      : "fui-animal-card__meta-value"
                  }
                >
                  {row.value}
                </span>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {chipLabel !== undefined ? (
        <div className="fui-animal-card__footer">
          <div className="fui-animal-card__chip">
            <span className="fui-animal-card__chip-label">{chipLabel}</span>
            {chipHref && chipValue ? (
              <a
                className="fui-animal-card__chip-value fui-animal-card__link"
                href={chipHref}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                {chipValue}
              </a>
            ) : (
              <span className="fui-animal-card__chip-value">{chipValue ?? "—"}</span>
            )}
          </div>
          {showDetailsButton && onOpen ? (
            <button
              type="button"
              className="fui-animal-card__details"
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            >
              {detailsLabel} ›
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
